import React from 'react';
import { useIntl } from 'react-intl-next';

import type { ClassificationOption } from '@atlassian/data-classification-level';
import { EditPopup } from '@atlassian/data-classification-level';

import type { ClassificationLevel } from '../ClassificationRadioOption.tsx';
import { useDefaultAndSpaceClassificationLevels } from '../hooks/useDefaultAndSpaceClassificationLevels.ts';
import { getAllClassificationLevels } from '../utils/getAllClassificationLevels.ts';
import { useComputeDefaultClassificationDropdownOption } from '../hooks/useComputeDefaultClassificationDropdownOption.ts';

import { sharedMessages } from './sharedMessages.ts';

type EditViewProps = {
	spaceKey: string;
	onEditCancel: () => void;
	onEditSubmit: (classification: ClassificationOption) => void;
	selectedOption?: ClassificationOption;
	loading?: boolean;
	hasError?: boolean;
	shouldShowHeading?: boolean;
};

export const ClassificationEditViewPopup = ({
	spaceKey,
	selectedOption: selectedOptionProp,
	onEditCancel,
	onEditSubmit,
	loading = false,
	shouldShowHeading = true,
}: EditViewProps) => {
	const { formatMessage } = useIntl();

	const {
		defaultClassification: spaceDefaultClassification,
		classificationLevels,
		getClassificationLevelFromId,
		expectedError,
		loading: spaceAndDefaultLoading,
	} = useDefaultAndSpaceClassificationLevels({
		spaceKey,
		shouldPreloadData: true,
	});

	const {
		defaultClassificationOption: defaultOption,
		error: defaultClassificationError,
		loading: orgDefaultLoading,
	} = useComputeDefaultClassificationDropdownOption({
		spaceDefaultClassificationLevel: spaceDefaultClassification,
		getClassificationLevelFromId,
	});

	const selectedOption = selectedOptionProp || defaultOption;

	const allClassificationLevels: ClassificationLevel[] = [
		...getAllClassificationLevels(classificationLevels),
		defaultOption,
	];

	const hasError = !!expectedError || !!defaultClassificationError;

	// Note: If we do not re-render the EditPopup after loading is completed,
	// the selectedOption may not be correct, as its only used in EditPopup
	// for the initial useState value; it is not a controlled value.
	const combinedLoadingCondition = loading || spaceAndDefaultLoading || orgDefaultLoading;
	if (combinedLoadingCondition) {
		return null;
	}

	return (
		<EditPopup
			options={allClassificationLevels}
			selectedOption={selectedOption}
			onEditCancel={onEditCancel}
			onEditSubmit={onEditSubmit}
			loading={combinedLoadingCondition}
			errorMessage={hasError ? formatMessage(sharedMessages.fetchErrorTextPrimary) : undefined}
			shouldShowHeading={shouldShowHeading}
			menuPosition="fixed"
		/>
	);
};
